import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ConfigSectionModel, ProductModel, SectionOptionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GetCollectiveProductContext from "@store/Components/Common/ProductContext/GetCollectiveProductContext";
import GetOptionalProductContext from "@store/Components/Common/ProductContext/GetOptionalProductContext";
import ProductPrice from "@store/Overrides/Components/ProductPrice";
import { getShowNormalPrice, getShowRetailPrice } from "@store/Store/Context/ContextSelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { contentSpacingStyles } from "@store/theme/common/mixins";
import { productTableProps, StoreProductTableProps } from "@store/theme/common/props/productTable";
import { TYPOGRAPHY } from "@store/theme/design";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type OwnProps = {
    configSection?: ConfigSectionModel;
};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StoreSectionOptionModel extends SectionOptionModel {
    product?: ProductModel;
}

const mapStateToProps = (state: StoreApplicationState, { configSection }: OwnProps) => {
    const productIds = (configSection?.sectionOptions?.filter(x => x.productId).map(x => x.productId) ||
        []) as string[]; // Typing necessary, we filter out null values but the linter does not care

    let parameters;
    let sectionOptions: StoreSectionOptionModel[] = configSection?.sectionOptions || [];
    let isLoading = false;
    let isError = false;

    if (productIds.length) {
        parameters = { productIds, pageSize: productIds.length };
        const productsDataView = getProductsDataView(state, parameters);
        isLoading = productsDataView.isLoading || !(productsDataView as any).properties;
        isError = !isLoading && !productsDataView.value;

        if (!isError) {
            sectionOptions = sectionOptions
                ?.map(option => ({
                    ...option,
                    product: productsDataView.value?.find(product => product.id === option.productId),
                }))
                .sort((a, b) => a.sortOrder - b.sortOrder);
        }
    }

    return {
        parameters,
        sectionOptions,
        isLoading,
        isError,
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        showNormalPrice: getShowNormalPrice(state),
        showRetailPrice: getShowRetailPrice(state),
    };
};

const mapDispatchToProps = {
    loadProducts,
};

export interface StoreConfigurationTableStyles extends StoreProductTableProps {
    wrapper?: InjectableCss;
    configurationPrice?: TypographyPresentationProps;
}

const styles: StoreConfigurationTableStyles = {
    ...productTableProps,
    wrapper: {
        css: css`
            ${contentSpacingStyles()}
        `,
    },
    configurationPrice: {
        forwardAs: "span",
        weight: TYPOGRAPHY.weight.bold,
    },
};

export const configurationTableStyles = styles;

type ConfigurationOptionProps = {
    option: StoreSectionOptionModel;
};

const ProductNumber: React.FC<ConfigurationOptionProps> = ({ option }) => {
    const productNumber = option.product?.productNumber || option.name;
    const url = option.product?.canonicalUrl;

    if (url) {
        return (
            <Link href={url} target="_blank">
                {productNumber}
            </Link>
        );
    }

    return <>{productNumber}</>;
};

const StoreConfigurationTable: React.FC<Props> = ({
    parameters,
    sectionOptions,
    isLoading,
    isError,
    isSignedIn,
    loadProducts,
    showNormalPrice,
    showRetailPrice,
}) => {
    useEffect(() => {
        if (parameters) {
            loadProducts(parameters);
        }
    }, []);

    if (isLoading) {
        return null;
    }

    if (isError) {
        return (
            <Typography {...styles.noResults}>
                {translate("The items could not be found, please try again later.")}
            </Typography>
        );
    }

    if (!sectionOptions.length) {
        return <Typography {...styles.noResults}>{translate("There are no items available.")}</Typography>;
    }

    const products = sectionOptions.map(x => x.product).filter(x => x);
    const hasProducts = products.length > 0;

    return (
        <>
            <StyledWrapper {...styles.tableWrapper}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader>{translate("Product #")}</DataTableHeader>
                        {hasProducts && <DataTableHeader>{translate("Store_MfgNumber")}</DataTableHeader>}
                        <DataTableHeader>{translate("Description")}</DataTableHeader>
                        <DataTableHeader>{translate("Included Qty.")}</DataTableHeader>
                        {isSignedIn && (
                            <>
                                {hasProducts && <DataTableHeader>{translate("Availability")}</DataTableHeader>}
                                {showNormalPrice && <DataTableHeader>{translate("Price")}</DataTableHeader>}
                                {showRetailPrice && <DataTableHeader>{translate("Retail Price")}</DataTableHeader>}
                                <DataTableHeader></DataTableHeader>
                            </>
                        )}
                    </DataTableHead>
                    <DataTableBody>
                        <GetCollectiveProductContext products={products}>
                            {sectionOptions.map(option => (
                                <GetOptionalProductContext product={option.product} key={option.id}>
                                    <DataTableRow>
                                        <DataTableCell>
                                            <ProductNumber option={option} />
                                        </DataTableCell>
                                        {hasProducts && (
                                            <DataTableCell>{option.product?.manufacturerItem}</DataTableCell>
                                        )}
                                        <DataTableCell {...styles.tableCellDescription}>
                                            {option.product?.productTitle || option.description}
                                        </DataTableCell>
                                        <DataTableCell {...styles.tableCellQuantity}>
                                            {(option as any).quantity}
                                        </DataTableCell>
                                        {isSignedIn && (
                                            <>
                                                {hasProducts && (
                                                    <DataTableCell>
                                                        {option.product && <ProductContextAvailability />}
                                                    </DataTableCell>
                                                )}
                                                {showNormalPrice && (
                                                    <DataTableCell>
                                                        {option.product && (
                                                            <ProductPrice showLabel={false} showNormalOnly={true} />
                                                        )}
                                                        {!option.product && option.price > 0 && (
                                                            <Typography {...styles.configurationPrice}>
                                                                <LocalizedCurrency amount={option.price} />
                                                            </Typography>
                                                        )}
                                                    </DataTableCell>
                                                )}
                                                {showRetailPrice && (
                                                    <DataTableCell>
                                                        {option.product && (
                                                            <ProductPrice
                                                                showRetailOnly={true}
                                                                showRetailLabel={false}
                                                            />
                                                        )}
                                                    </DataTableCell>
                                                )}
                                                <DataTableCell {...(hasProducts ? styles.tableCellControls : {})}>
                                                    {option.product ? (
                                                        <StyledWrapper {...styles.itemControls}>
                                                            <ProductQuantityOrdered labelOverride="" />
                                                            <ProductAddToCartButton labelOverride={translate("Add")} />
                                                        </StyledWrapper>
                                                    ) : (
                                                        <Typography {...styles.itemNote}>
                                                            {translate("Call Branch*")}
                                                        </Typography>
                                                    )}
                                                </DataTableCell>
                                            </>
                                        )}
                                    </DataTableRow>
                                </GetOptionalProductContext>
                            ))}
                        </GetCollectiveProductContext>
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            {isSignedIn && sectionOptions.some(x => !x.product) && (
                <Typography {...styles.globalNote}>
                    {translate("*Please contact local branch to inquire about this item.")}
                </Typography>
            )}
        </>
    );
};

const Component: React.FC<Props> = props => (
    <StyledWrapper {...styles.wrapper}>
        <LoadingOverlay loading={props.isLoading} {...styles.loadingOverlay}>
            <StoreConfigurationTable {...props} />
        </LoadingOverlay>
    </StyledWrapper>
);

export default connect(mapStateToProps, mapDispatchToProps)(Component);
